import { IconWalls } from "$root/components/IconWall";
import SmallHeatmapChart from "$root/components/SmallHeatmapChart/SmallHeatmapChart";
import { hmmRepository } from "$root/functional-areas/hmm/repository";
import { PaletteColors } from "$root/styles/themePalette";
import { type ContextContent, withContext } from "$root/utils";
import { RegionContext } from "$root/widgets-architecture/contexts/region";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { ScrollWrapper, Text } from "@mdotm/shadcn/legacy/components";
import type { Chart } from "highcharts";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./style.css";

const RegimeTransitionMatrix2WBlock = (props: ContextContent<typeof RegionContext>) => {
	const { t } = useTranslation();
	const days = "14";
	const { region } = props;
	const chartRef = useRef<{ chart: Chart } | null>(null);

	const { isLoading, isFetching, isError, data } = hmmRepository.regimesTransitionMatrices.useQuery(region, { days });

	const { conditionalHistoricalTransition, historicalTransition } = data ?? {};

	const conditionalTransitions = useMemo(() => {
		const { data: phase } = conditionalHistoricalTransition ?? {};
		if (!phase) {
			return;
		}
		return [
			[0, 0, phase.growth],
			[1, 0, phase.lateralPhase],
			[2, 0, phase.highStress],
		];
	}, [conditionalHistoricalTransition]);

	const historicalTransitions = useMemo(() => {
		const { growth, lateralPhase, highStress } = historicalTransition ?? {};
		if (!growth || !lateralPhase || !highStress) {
			return;
		}
		return [
			[0, 0, growth.growth],
			[0, 1, lateralPhase.growth],
			[0, 2, highStress.growth],
			[1, 0, growth.lateralPhase],
			[1, 1, lateralPhase.lateralPhase],
			[1, 2, highStress.lateralPhase],
			[2, 0, growth.highStress],
			[2, 1, lateralPhase.highStress],
			[2, 2, highStress.highStress],
		];
	}, [historicalTransition]);

	const points = { conditionalTransitions, historicalTransitions };

	// const matrixPhase = useCallback((numberPhase: number | undefined) => {
	// 	if (!numberPhase) {
	// 		return;
	// 	}
	// 	const DTOMatricePhase: TmatrixPhase = {
	// 		1: { name: "Growth", color: PaletteColors.GREENY_BLUE },
	// 		2: { name: "Lateral Phase", color: PaletteColors.YELLOW },
	// 		3: { name: "High Stress", color: PaletteColors.NEON_RED },
	// 	};

	// 	return [DTOMatricePhase[numberPhase]];
	// }, []);

	useWidgetOptions(
		() => ({
			title: t("REGIME_TRANSITION_MATRIX.2W.TITLE"),
		}),
		[t],
	);

	return (
		<>
			<div>
				<ScrollWrapper style={{ maxHeight: 120 }}>{t("REGIME_TRANSITION_MATRIX.2W.DESCRIPTION")}</ScrollWrapper>
			</div>
			{isError ? (
				<IconWalls.ErrorData />
			) : isLoading || isFetching ? (
				<IconWalls.LoadingData />
			) : (
				<div style={{ marginTop: 20 }}>
					<div className="historical-transitions">
						<div className="mb-4 mt-2">
							<Text type="Body/L/Bold" style={{ paddingRight: 5 }}>
								{t("REGIME_TRANSITION_MATRIX.2W.HISTORICAL_TRANSITIONS.TITLE")}
							</Text>
						</div>
						<SmallHeatmapChart
							ref={chartRef}
							legend
							data={points.historicalTransitions ?? []}
							xCategories={[
								{ name: t("REGIME_TRANSITION_MATRIX.AXIS.GROWTH"), color: PaletteColors.GREENY_BLUE },
								{ name: t("REGIME_TRANSITION_MATRIX.AXIS.LATERAL_PHASE"), color: PaletteColors.YELLOW },
								{ name: t("REGIME_TRANSITION_MATRIX.AXIS.HIGH_STRESS"), color: PaletteColors.NEON_RED },
							]}
							yCategories={[
								{ name: t("REGIME_TRANSITION_MATRIX.AXIS.GROWTH"), color: PaletteColors.GREENY_BLUE },
								{ name: t("REGIME_TRANSITION_MATRIX.AXIS.LATERAL_PHASE"), color: PaletteColors.YELLOW },
								{ name: t("REGIME_TRANSITION_MATRIX.AXIS.HIGH_STRESS"), color: PaletteColors.NEON_RED },
							]}
						/>
					</div>
				</div>
			)}
		</>
	);
};

export default withContext(RegionContext)(RegimeTransitionMatrix2WBlock);
